import { useCallback, useEffect, useRef, useState } from 'react';
import { installerApi, uploadViaSignedUrl, putSigned } from '../../utils/installerApi';
import { listQueued, listStageOutbox, flushQueue, flushStageOutbox, currentInstaller, pruneStageData } from './offlineQueue';
import { listDrafts } from './stageDrafts';
import { toFa } from '../../utils/faFormat';
import { reportClientEvent } from '../../lib/clientLog';

export function useOfflineQueue(token, onFlushed) {
  const [queuedIds, setQueuedIds] = useState(() => new Set());
  const [drafts, setDrafts] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [notice, setNotice] = useState(null);
  const onFlushedRef = useRef(onFlushed);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  useEffect(() => { onFlushedRef.current = onFlushed; });
  const resolveId = useCallback(async () => {
    if (!token) return null;
    return currentInstaller(token) || (await installerApi.me(token)).id;
  }, [token]);
  const refresh = useCallback(async () => {
    const installerId = await resolveId();
    if (!installerId) return;
    const [items, stages, localDrafts] = await Promise.all([listQueued(installerId), listStageOutbox(installerId), listDrafts(installerId)]);
    if (tokenRef.current !== token) return;
    setQueuedIds(new Set([...items, ...stages.filter((i) => !i.error)].map((i) => i.jobId)));
    setOutbox(stages); setDrafts(localDrafts);
  }, [resolveId, token]);
  const flush = useCallback(async () => {
    if (!token || !navigator.onLine) return;
    try {
      const installerId = await resolveId();
      if (tokenRef.current !== token) return;
      const results = [...await flushQueue({ token, installerId, uploadViaSignedUrl, completeJob: installerApi.completeJob }),
        ...await flushStageOutbox({ token, installerId, captureTicket: installerApi.captureTicket, captureUploadUrl: installerApi.captureUploadUrl,
          putSigned, submitStageAttempt: installerApi.submitStageAttempt, fetchJob: installerApi.job })];
      if (tokenRef.current !== token) return;
      await refresh();
      const messages = results.filter((r) => r.error).map((r) => r.error);
      if (messages.length) setNotice(messages[0]);
      if (onFlushedRef.current && results.some((r) => r.ok || r.dropped)) onFlushedRef.current(results);
    } catch (e) {
      // Offline or expired sessions never replay an unbound queue; say why it stopped.
      reportClientEvent('outbox.flush_failed', { status: e?.status, code: e?.code, rid: e?.requestId });
    }
  }, [token, resolveId, refresh]);
  useEffect(() => {
    let cancelled = false;
    setQueuedIds(new Set()); setDrafts([]); setOutbox([]);
    if (token) resolveId().then(async (id) => {
      const count = await pruneStageData(id);
      if (cancelled) return;
      if (count) setNotice(`${toFa(count)} ارسال قدیمی‌تر از ۷ روز از گوشی پاک شد.`);
      await refresh(); await flush();
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [token, resolveId, refresh, flush]);
  useEffect(() => {
    const onOnline = () => { flush(); };
    const onVisible = () => { if (document.visibilityState === 'visible') { refresh().catch(() => {}); flush(); } };
    const onMsg = (e) => { if (e.data?.type === 'FLUSH_QUEUE') flush(); };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    navigator.serviceWorker?.addEventListener('message', onMsg);
    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
      navigator.serviceWorker?.removeEventListener('message', onMsg);
    };
  }, [flush, refresh]);
  return { queuedIds, drafts, outbox, notice, flush, refresh };
}
